/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import { useEffect, useState, useMemo } from 'react';
import LocalstorageService from '../../helpers/localstorage-service';
import { CompanyId, JobRole, USER_ROLE } from '../../constants';
import { Button, Col, FormGroup, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap';
import MachineService from '../../services/machines';
import EmployeeService from '../../services/employee';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import ReactSelect from 'react-select';
import { NumericFormat } from 'react-number-format';
import ReactDatePicker from 'react-datepicker';
import { getIsraelBusinessDate } from '../../helpers/dateUtils';

const HEBREW_DAYS = ['\u05e8\u05d0\u05e9\u05d5\u05df', '\u05e9\u05e0\u05d9', '\u05e9\u05dc\u05d9\u05e9\u05d9', '\u05e8\u05d1\u05d9\u05e2\u05d9', '\u05d7\u05de\u05d9\u05e9\u05d9', '\u05e9\u05d9\u05e9\u05d9', '\u05e9\u05d1\u05ea'];

const AlternativeLoadingTerminals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [setPageTitle, setCrumbItem] = useOutletContext();
  const companyId = LocalstorageService.getItem(CompanyId);
  const roleId = LocalstorageService.getItem(JobRole);

  // Loading date = business date (today if before 18:00, tomorrow if after 18:00)
  const loadingDate = useMemo(() => {
    const bd = getIsraelBusinessDate();
    bd.setHours(0, 0, 0, 0);
    return bd;
  }, []);

  // Mode: 'all' = all employees, 'specific' = specific employees
  const [mode, setMode] = useState('all');
  const [empList, setEmpList] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [targetDate, setTargetDate] = useState(null);
  const [dayWeights, setDayWeights] = useState([]);
  const [dayFraction, setDayFraction] = useState(0.6);

  // Mode 2: terminal selection
  const [employeeTerminals, setEmployeeTerminals] = useState([]);
  const [checkedTerminals, setCheckedTerminals] = useState({});

  // Overwrite modal
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);

  useEffect(() => {
    setPageTitle(t('ALT_LOADING.TITLE'));
    setCrumbItem([
      { title: t('APPLICATION_TITLE'), link: '#' },
      { title: t('ALT_LOADING.TITLE'), link: '#' },
    ]);
    getEmployeeFn();
    getDayFractionFn();
  }, []);

  // When target date changes, generate day weights from loadingDate to targetDate
  useEffect(() => {
    if (targetDate) {
      generateDayWeights(targetDate);
    } else {
      setDayWeights([]);
    }
  }, [targetDate, dayFraction]);

  // When mode or selected employees change, fetch terminals for Mode 2
  useEffect(() => {
    if (mode === 'specific' && selectedEmployees.length > 0) {
      fetchEmployeeTerminals();
    } else {
      setEmployeeTerminals([]);
      setCheckedTerminals({});
    }
  }, [mode, selectedEmployees]);

  async function getEmployeeFn() {
    await EmployeeService.listEmployee({ companyId })
      .then((response) => {
        const { status, dataList } = response.data;
        if (status === 400) return;
        if (dataList && dataList.length) {
          const data = dataList.map((item) => ({
            id: item.id,
            label: `${item.firstName} ${item.lastName}`,
            value: item.id,
          }));
          setEmpList(data);
        }
      });
  }

  async function getDayFractionFn() {
    await MachineService.getDayFraction({ companyId })
      .then((response) => {
        const { status, dayFraction: df } = response.data;
        if (status === 200 && df) {
          setDayFraction(df);
        }
      })
      .catch(() => {});
  }

  function generateDayWeights(target) {
    const targetD = new Date(target);
    targetD.setHours(0, 0, 0, 0);

    if (targetD < loadingDate) {
      setDayWeights([]);
      return;
    }

    const weights = [];
    const current = new Date(loadingDate);

    while (current <= targetD) {
      const isTarget = current.getTime() === targetD.getTime();
      weights.push({
        date: new Date(current),
        dayName: HEBREW_DAYS[current.getDay()],
        weight: isTarget ? dayFraction : 1.0,
        isTargetDay: isTarget,
      });
      current.setDate(current.getDate() + 1);
    }
    setDayWeights(weights);
  }

  function updateWeight(index, value) {
    const val = parseFloat(value);
    if (isNaN(val) || val < 0 || val > 2) return;
    const updated = [...dayWeights];
    updated[index] = { ...updated[index], weight: val };
    setDayWeights(updated);
  }

  async function fetchEmployeeTerminals() {
    dispatch(setLoadingOverlay(true));
    const empIds = selectedEmployees.map((e) => e.value).join(',');
    await MachineService.getEmployeeTerminals({ companyId, employeeIds: empIds })
      .then((response) => {
        const { status, dataList } = response.data;
        if (status === 200 && dataList) {
          setEmployeeTerminals(dataList);
          const checked = {};
          dataList.forEach((t) => {
            checked[t.terminalNumber] = true;
          });
          setCheckedTerminals(checked);
        }
      })
      .catch(() => toast.error(t('ALT_LOADING.ERROR')))
      .finally(() => dispatch(setLoadingOverlay(false)));
  }

  function toggleTerminal(terminalNumber) {
    setCheckedTerminals((prev) => ({
      ...prev,
      [terminalNumber]: !prev[terminalNumber],
    }));
  }

  function toggleAllTerminals(checked) {
    const updated = {};
    employeeTerminals.forEach((t) => {
      updated[t.terminalNumber] = checked;
    });
    setCheckedTerminals(updated);
  }

  function getLoadingDayName(terminal) {
    const days = [
      { key: 'sunday', name: HEBREW_DAYS[0] },
      { key: 'monday', name: HEBREW_DAYS[1] },
      { key: 'tuesday', name: HEBREW_DAYS[2] },
      { key: 'wednesday', name: HEBREW_DAYS[3] },
      { key: 'thursday', name: HEBREW_DAYS[4] },
      { key: 'friday', name: HEBREW_DAYS[5] },
      { key: 'saturday', name: HEBREW_DAYS[6] },
    ];
    return days.filter((d) => terminal[d.key] === 1).map((d) => d.name).join(', ') || '-';
  }

  async function handleCalculate() {
    if (!targetDate) {
      toast.error(t('ALT_LOADING.NO_DATE_SELECTED'));
      return;
    }
    if (dayWeights.length === 0) {
      toast.error(t('ALT_LOADING.NO_DATE_SELECTED'));
      return;
    }
    if (mode === 'specific' && selectedEmployees.length === 0) {
      toast.error(t('ALT_LOADING.NO_EMPLOYEES_SELECTED'));
      return;
    }

    // Check existing calculations for the LOADING date
    const dateS = formatDateForApi(loadingDate);
    try {
      const checkRes = await MachineService.checkExistingCalculation({ companyId, dateS });
      if (checkRes.data.recordCount > 0) {
        setShowOverwriteModal(true);
        return;
      }
    } catch {
      // continue
    }

    await runCalculation();
  }

  async function runCalculation() {
    dispatch(setLoadingOverlay(true));
    setShowOverwriteModal(false);

    // dateS = loading date (business date), NOT the target date
    const dateS = formatDateForApi(loadingDate);
    const weights = dayWeights.map((dw) => ({
      date: formatDateForApi(dw.date),
      weight: dw.weight,
    }));

    // Build terminal numbers for Mode 2
    let terminalNumbers = null;
    if (mode === 'specific') {
      const selected = Object.entries(checkedTerminals)
        .filter(([_, checked]) => checked)
        .map(([tn]) => tn);
      if (selected.length > 0) {
        terminalNumbers = selected;
      }
    }

    // Build employeeIds for Mode 2 (comma-separated)
    let employeeIds = null;
    if (mode === 'specific' && selectedEmployees.length > 0) {
      employeeIds = selectedEmployees.map((e) => e.value).join(',');
    }

    const payload = {
      companyId,
      employeeId: 0,
      dateS,
      dayWeights: weights,
      terminalNumbers,
      employeeIds,
      editRow: false,
      newTotalAmount: 0,
    };

    await MachineService.alternativeLoadingCalculation(payload)
      .then((response) => {
        const { status } = response.data;
        if (status === 200) {
          toast.success(t('ALT_LOADING.CALC_SUCCESS'));
          // Navigate to existing loading terminal list to view results
          navigate('/loading-terminal/list');
        } else {
          toast.error(response.data.message || t('ALT_LOADING.ERROR'));
        }
      })
      .catch(() => toast.error(t('ALT_LOADING.ERROR')))
      .finally(() => dispatch(setLoadingOverlay(false)));
  }

  function formatDateForApi(date) {
    const d = new Date(date);
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
  }

  function formatDateDisplay(date) {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
  }

  // minDate for date picker = loading date (business date)
  const minDate = loadingDate;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <h4 className="mb-3">{t('ALT_LOADING.TITLE')}</h4>

        {/* Info: Show loading date */}
        <div className="alert alert-info mb-3">
          {t('ALT_LOADING.LOADING_DATE_INFO')}: <strong>{formatDateDisplay(loadingDate)}</strong>
        </div>

        {/* Step 1: Mode Selection */}
        <Row className="mb-3">
          <Col md={6}>
            <FormGroup check inline>
              <Input
                type="radio"
                name="mode"
                checked={mode === 'all'}
                onChange={() => { setMode('all'); setSelectedEmployees([]); }}
              />{' '}
              <label>{t('ALT_LOADING.MODE_ALL')}</label>
            </FormGroup>
            <FormGroup check inline>
              <Input
                type="radio"
                name="mode"
                checked={mode === 'specific'}
                onChange={() => { setMode('specific'); }}
              />{' '}
              <label>{t('ALT_LOADING.MODE_SPECIFIC')}</label>
            </FormGroup>
          </Col>
        </Row>

        {/* Step 2: Employee Selection (Mode 2 only) */}
        {mode === 'specific' && (
          <Row className="mb-3">
            <Col md={6}>
              <label>{t('ALT_LOADING.SELECT_EMPLOYEES')}</label>
              <ReactSelect
                isMulti
                options={empList}
                value={selectedEmployees}
                onChange={(options) => { setSelectedEmployees(options || []); }}
                placeholder={t('ALT_LOADING.SELECT_EMPLOYEES')}
              />
            </Col>
          </Row>
        )}

        {/* Step 3: Target Date */}
        <Row className="mb-3">
          <Col md={3}>
            <label>{t('ALT_LOADING.TARGET_DATE')}</label>
            <ReactDatePicker
              className="form-control"
              selected={targetDate}
              onChange={(date) => { setTargetDate(date); }}
              dateFormat="dd/MM/yyyy"
              minDate={minDate}
              placeholderText={t('ALT_LOADING.TARGET_DATE')}
            />
          </Col>
        </Row>

        {/* Step 4: Day Weights Table */}
        {dayWeights.length > 0 && (
          <Row className="mb-3">
            <Col md={6}>
              <h5>{t('ALT_LOADING.WEIGHTS_TITLE')}</h5>
              <Table bordered size="sm" className="table-striped">
                <thead>
                  <tr>
                    <th>{t('ALT_LOADING.DAY_COLUMN')}</th>
                    <th>{t('ALT_LOADING.DATE_COLUMN')}</th>
                    <th style={{ width: '120px' }}>{t('ALT_LOADING.WEIGHT_COLUMN')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dayWeights.map((dw, idx) => (
                    <tr key={idx} className={dw.isTargetDay ? 'table-info' : ''}>
                      <td>{dw.dayName}</td>
                      <td>{formatDateDisplay(dw.date)}</td>
                      <td>
                        <NumericFormat
                          className="form-control form-control-sm"
                          value={dw.weight}
                          decimalScale={2}
                          allowNegative={false}
                          isAllowed={(values) => {
                            const { floatValue } = values;
                            return floatValue === undefined || (floatValue >= 0 && floatValue <= 2);
                          }}
                          onValueChange={(values) => updateWeight(idx, values.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        )}

        {/* Step 5: Terminal Selection (Mode 2 only) */}
        {mode === 'specific' && employeeTerminals.length > 0 && (
          <Row className="mb-3">
            <Col md={8}>
              <h5>{t('ALT_LOADING.TERMINAL_SELECTION')}</h5>
              <div className="mb-2">
                <FormGroup check inline>
                  <Input
                    type="checkbox"
                    checked={Object.values(checkedTerminals).every(Boolean)}
                    onChange={(e) => toggleAllTerminals(e.target.checked)}
                  />{' '}
                  <label>{t('ALT_LOADING.SELECT_ALL')}</label>
                </FormGroup>
              </div>
              <Table bordered size="sm" className="table-striped">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>{t('ALT_LOADING.EMPLOYEE_COLUMN')}</th>
                    <th>{t('ALT_LOADING.TERMINAL_COLUMN')}</th>
                    <th>{t('ALT_LOADING.BUSINESS_NAME_COLUMN')}</th>
                    <th>{t('ALT_LOADING.LOADING_DAY_COLUMN')}</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeTerminals.map((terminal, idx) => (
                    <tr key={idx}>
                      <td className="text-center">
                        <Input
                          type="checkbox"
                          checked={!!checkedTerminals[terminal.terminalNumber]}
                          onChange={() => toggleTerminal(terminal.terminalNumber)}
                        />
                      </td>
                      <td>{terminal.employeeName}</td>
                      <td>{terminal.terminalNumber}</td>
                      <td>{terminal.locationName}</td>
                      <td>{getLoadingDayName(terminal)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        )}

        {/* Calculate Button */}
        {dayWeights.length > 0 && (
          <Row className="mb-4">
            <Col>
              <Button color="primary" size="lg" onClick={handleCalculate}>
                {t('ALT_LOADING.CALCULATE_BTN')}
              </Button>
            </Col>
          </Row>
        )}

        {/* Overwrite Confirmation Modal */}
        <Modal isOpen={showOverwriteModal} toggle={() => setShowOverwriteModal(false)} centered>
          <ModalHeader toggle={() => setShowOverwriteModal(false)}>
            {t('ALT_LOADING.OVERWRITE_TITLE')}
          </ModalHeader>
          <ModalBody>{t('ALT_LOADING.OVERWRITE_MSG')}</ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={runCalculation}>
              {t('ALT_LOADING.CONFIRM')}
            </Button>
            <Button color="secondary" onClick={() => setShowOverwriteModal(false)}>
              {t('ALT_LOADING.CANCEL')}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default AlternativeLoadingTerminals;
